import {
  Alert,
  Badge,
  Button,
  Code,
  DialogActionTrigger,
  DialogTitle,
  Flex,
  IconButton,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FiCopy, FiEye, FiEyeOff, FiRefreshCw } from "react-icons/fi"

import { OrganizationsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Toaster, toaster } from "@/components/ui/toaster"

interface OrganizationAPIKeysProps {
  orgId: string
  orgName: string
  appId: string
}

interface SecretKeyState {
  secret_key: string | null
  shown_at: number | null
}

function OrganizationAPIKeys({
  orgId,
  orgName,
  appId,
}: OrganizationAPIKeysProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showAppId, setShowAppId] = useState(false)
  const [showSecretKey, setShowSecretKey] = useState(false)
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)
  const [secretKey, setSecretKey] = useState<SecretKeyState>({
    secret_key: null,
    shown_at: null,
  })
  const queryClient = useQueryClient()

  const regenerateMutation = useMutation({
    mutationFn: () =>
      OrganizationsService.regenerateOrganizationKeys({ orgId }),
    onSuccess: (data) => {
      // Store the new secret key (it will only be shown this once!)
      setSecretKey({
        secret_key: data.secret_key,
        shown_at: Date.now(),
      })
      setShowRegenerateConfirm(false)
      setShowSecretKey(true)
      toaster.create({
        title: "Keys regenerated successfully",
        description:
          "Save your new secret key now - you won't be able to see it again!",
        type: "success",
        duration: 10000,
      })
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
    onError: (err: ApiError) => {
      toaster.create({
        title: "Error regenerating keys",
        description: err.message,
        type: "error",
      })
    },
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toaster.create({
      title: `${label} copied`,
      description: "Copied to clipboard",
      type: "success",
      duration: 2000,
    })
  }

  const handleRegenerate = () => {
    regenerateMutation.mutate()
  }

  const maskKey = (key: string, show: boolean) => {
    if (show) return key
    return "•".repeat(Math.min(key.length, 40))
  }

  // Check if we have a recently shown secret key (within last 5 minutes)
  const hasRecentSecretKey =
    secretKey.secret_key &&
    secretKey.shown_at &&
    Date.now() - secretKey.shown_at < 5 * 60 * 1000

  return (
    <>
      <Toaster />
      <DialogRoot
        size={{ base: "sm", md: "xl" }}
        placement="center"
        open={isOpen}
        onOpenChange={({ open }) => {
          setIsOpen(open)
          if (!open) {
            // Reset visibility when closing
            setShowAppId(false)
            setShowSecretKey(false)
            setShowRegenerateConfirm(false)
          }
        }}
      >
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            API Keys
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Keys - {orgName}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={6} align="stretch">
              {/* Warning banner */}
              <Alert.Root status="warning">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>Security Notice</Alert.Title>
                  <Alert.Description>
                    Keep your API keys secure. The secret key is only shown once
                    when generated. If you've lost it, you'll need to regenerate
                    new keys.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>

              {/* App ID */}
              <VStack gap={2} align="stretch">
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold">App ID</Text>
                  <Badge colorPalette="blue">Public</Badge>
                </Flex>
                <Flex gap={2} align="center">
                  <Input
                    value={maskKey(appId, showAppId)}
                    readOnly
                    fontFamily="mono"
                    fontSize="sm"
                  />
                  <IconButton
                    aria-label={showAppId ? "Hide App ID" : "Show App ID"}
                    onClick={() => setShowAppId(!showAppId)}
                    variant="ghost"
                    size="sm"
                  >
                    {showAppId ? <FiEyeOff /> : <FiEye />}
                  </IconButton>
                  <IconButton
                    aria-label="Copy App ID"
                    onClick={() => copyToClipboard(appId, "App ID")}
                    variant="ghost"
                    size="sm"
                  >
                    <FiCopy />
                  </IconButton>
                </Flex>
                <Text fontSize="sm" color="gray.600">
                  Public application identifier for HMAC authentication
                </Text>
              </VStack>

              {/* Secret Key */}
              <VStack gap={2} align="stretch">
                <Flex justify="space-between" align="center">
                  <Text fontWeight="bold">Secret Key</Text>
                  <Badge colorPalette="red">Secret</Badge>
                </Flex>
                {hasRecentSecretKey ? (
                  <>
                    <Flex gap={2} align="center">
                      <Input
                        value={maskKey(secretKey.secret_key!, showSecretKey)}
                        readOnly
                        fontFamily="mono"
                        fontSize="sm"
                      />
                      <IconButton
                        aria-label={
                          showSecretKey ? "Hide secret key" : "Show secret key"
                        }
                        onClick={() => setShowSecretKey(!showSecretKey)}
                        variant="ghost"
                        size="sm"
                      >
                        {showSecretKey ? <FiEyeOff /> : <FiEye />}
                      </IconButton>
                      <IconButton
                        aria-label="Copy secret key"
                        onClick={() =>
                          copyToClipboard(secretKey.secret_key!, "Secret Key")
                        }
                        variant="ghost"
                        size="sm"
                      >
                        <FiCopy />
                      </IconButton>
                    </Flex>
                    <Alert.Root status="success">
                      <Alert.Indicator />
                      <Alert.Content>
                        <Alert.Description>
                          <strong>Save this now!</strong> This is the only time
                          you'll see the secret key.
                        </Alert.Description>
                      </Alert.Content>
                    </Alert.Root>
                  </>
                ) : (
                  <>
                    <Code p={4} bg="bg.muted" borderRadius="md" fontSize="sm">
                      ••••••••••••••••••••••••••••••••••••••••
                    </Code>
                    <Text fontSize="sm" color="gray.600">
                      Secret key is hidden for security. Regenerate keys to get
                      a new secret key.
                    </Text>
                  </>
                )}
              </VStack>

              {/* Regenerate Section */}
              <VStack
                gap={3}
                align="stretch"
                pt={4}
                borderTop="1px solid"
                borderColor="border"
              >
                <Text fontWeight="bold">Regenerate Keys</Text>
                {showRegenerateConfirm ? (
                  <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Are you sure?</Alert.Title>
                      <Alert.Description>
                        <VStack align="stretch" gap={2}>
                          <Text>
                            Regenerating keys will immediately invalidate:
                          </Text>
                          <ul style={{ marginLeft: "1.5rem" }}>
                            <li>All existing embed tokens</li>
                            <li>All external integrations using old keys</li>
                            <li>Any active quiz sessions</li>
                          </ul>
                          <Text fontWeight="bold" color="red.600">
                            This action cannot be undone!
                          </Text>
                        </VStack>
                      </Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : (
                  <Text fontSize="sm" color="gray.600">
                    Generate new API and secret keys. This will invalidate all
                    existing keys.
                  </Text>
                )}
                <Flex gap={2}>
                  {showRegenerateConfirm ? (
                    <>
                      <Button
                        colorPalette="red"
                        onClick={handleRegenerate}
                        loading={regenerateMutation.isPending}
                        flex={1}
                      >
                        <FiRefreshCw />
                        Yes, Regenerate Keys
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setShowRegenerateConfirm(false)}
                        disabled={regenerateMutation.isPending}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      colorPalette="orange"
                      variant="outline"
                      onClick={() => setShowRegenerateConfirm(true)}
                    >
                      <FiRefreshCw />
                      Regenerate Keys
                    </Button>
                  )}
                </Flex>
              </VStack>

              {/* Usage Example */}
              <VStack
                gap={2}
                align="stretch"
                pt={4}
                borderTop="1px solid"
                borderColor="border"
              >
                <Text fontWeight="bold">Usage Example</Text>
                <Alert.Root status="warning" mb={4}>
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description fontSize="xs">
                      <strong>⚠️ URL Encoding:</strong> Generate signature with
                      RAW unencoded values (actual spaces), then URLSearchParams
                      will handle URL encoding automatically.
                    </Alert.Description>
                  </Alert.Content>
                </Alert.Root>
                <Code
                  p={4}
                  bg="bg.muted"
                  borderRadius="md"
                  fontSize="xs"
                  whiteSpace="pre-wrap"
                >
                  {`// Server-side signature generation (Node.js)
import crypto from 'crypto';

const SECRET_KEY = '${
                    hasRecentSecretKey
                      ? secretKey.secret_key
                      : "YOUR_SECRET_KEY"
                  }';
const APP_ID = '${appId}';

// STEP 1: Use RAW unencoded values (with actual spaces, not + signs)
const params = {
  program_name: 'Introduction to Python',  // Raw value with spaces
  student_email: 'student@example.com',
  student_name: 'John Doe',  // With spaces
  student_identifier: 'student-12345'
};

// STEP 2: Generate signature with RAW unencoded values
// Sort parameters by key and create message (key=value&key=value format)
const sortedKeys = Object.keys(params).sort();
const message = sortedKeys.map(key => \`\${key}=\${params[key]}\`).join('&');

// Generate HMAC-SHA256 signature
const signature = crypto
  .createHmac('sha256', SECRET_KEY)
  .update(message)
  .digest('hex');

// Add app_id and signature to URL parameters
params.app_id = APP_ID;
params.signature = signature;

// STEP 3: URL-encode when building query string (URLSearchParams handles this)
// Construct embed URL (frontend will send X-App-ID header)
const embedUrl = \`https://yourapp.com/embed/app?\${new URLSearchParams(params)}\`;
// Use embedUrl in iframe on your website`}
                </Code>
              </VStack>
            </VStack>
          </DialogBody>

          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="solid">Close</Button>
            </DialogActionTrigger>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  )
}

export default OrganizationAPIKeys
